export type LessonPhrase = {
  at: number;
  bangla: string;
  phonetic: string;
  meaning: string;
  prompt: string;
};

export type DialogueLine = {
  speaker: "You" | "Ammu" | "Family";
  bangla: string;
  phonetic: string;
  meaning: string;
};

export type PilotContent = {
  lessonId: number;
  scene: string;
  goal: string;
  phrases: LessonPhrase[];
  dialogue: DialogueLine[];
  sources: { label: string; href: string }[];
};

export const pilotContent: Record<number, PilotContent> = {
  1: {
    lessonId: 1,
    scene: "You walk into the room and greet Ammu.",
    goal: "Greet her, ask how she is, and ask her to slow down if you need it.",
    phrases: [
      {
        at: 0,
        bangla: "আসসালামু আলাইকুম, আম্মু।",
        phonetic: "assalamu alaikum, Ammu",
        meaning: "Peace be upon you, Ammu.",
        prompt: "How do you greet Ammu?"
      },
      {
        at: 145,
        bangla: "আপনি কেমন আছেন?",
        phonetic: "apni kemon achhen?",
        meaning: "How are you?",
        prompt: "Ask Ammu how she is."
      },
      {
        at: 310,
        bangla: "আমি ভালো আছি।",
        phonetic: "ami bhalo achhi",
        meaning: "I am well.",
        prompt: "Tell her that you are well."
      },
      {
        at: 475,
        bangla: "আমি অল্প বাংলা বলতে পারি।",
        phonetic: "ami olpo Bangla bolte pari",
        meaning: "I can speak a little Bangla.",
        prompt: "Say that you can speak a little Bangla."
      },
      {
        at: 650,
        bangla: "একটু আস্তে বলবেন?",
        phonetic: "ektu aste bolben?",
        meaning: "Would you speak a little slower?",
        prompt: "How do you ask her to speak more slowly?"
      },
      {
        at: 825,
        bangla: "আরেকবার বলবেন?",
        phonetic: "arekbar bolben?",
        meaning: "Would you say that again?",
        prompt: "Ask her to say it again."
      },
      {
        at: 1010,
        bangla: "আমি বুঝিনি।",
        phonetic: "ami bujhini",
        meaning: "I did not understand.",
        prompt: "Say that you did not understand."
      }
    ],
    dialogue: [
      {
        speaker: "You",
        bangla: "আসসালামু আলাইকুম, আম্মু।",
        phonetic: "assalamu alaikum, Ammu",
        meaning: "Peace be upon you, Ammu."
      },
      {
        speaker: "Ammu",
        bangla: "ওয়ালাইকুম আসসালাম। তুমি কেমন আছ?",
        phonetic: "walaikum assalam. tumi kemon achho?",
        meaning: "And peace be upon you. How are you?"
      },
      {
        speaker: "You",
        bangla: "আমি ভালো আছি। আপনি কেমন আছেন?",
        phonetic: "ami bhalo achhi. apni kemon achhen?",
        meaning: "I am well. How are you?"
      },
      {
        speaker: "Ammu",
        bangla: "আমি ভালো আছি।",
        phonetic: "ami bhalo achhi",
        meaning: "I am well."
      },
      {
        speaker: "You",
        bangla: "আমি অল্প বাংলা বলতে পারি। একটু আস্তে বলবেন?",
        phonetic: "ami olpo Bangla bolte pari. ektu aste bolben?",
        meaning: "I can speak a little Bangla. Would you speak a little slower?"
      }
    ],
    sources: [
      {
        label: "Peace Corps — Introduction to the Bangla Language",
        href: "https://fsi-languages.yojik.eu/languages/PeaceCorps/Bengali/Bengali.pdf"
      }
    ]
  },
  31: {
    lessonId: 31,
    scene: "Ammu asks when you will come over tomorrow.",
    goal: "Make a plan, give a time, and explain a small change.",
    phrases: [
      {
        at: 0,
        bangla: "কাল কী করবেন?",
        phonetic: "kal ki korben?",
        meaning: "What will you do tomorrow?",
        prompt: "Ask respectfully what she will do tomorrow."
      },
      {
        at: 155,
        bangla: "আমি বিকেলে আসব।",
        phonetic: "ami bikele ashbo",
        meaning: "I will come in the afternoon.",
        prompt: "Say that you will come in the afternoon."
      },
      {
        at: 320,
        bangla: "কখন আসবেন?",
        phonetic: "kokhon ashben?",
        meaning: "When will you come?",
        prompt: "Ask respectfully when someone will come."
      },
      {
        at: 485,
        bangla: "আমি পাঁচটার দিকে আসব।",
        phonetic: "ami panch-tar dike ashbo",
        meaning: "I will come around five.",
        prompt: "Say that you will come around five."
      },
      {
        at: 655,
        bangla: "আজ পারব না।",
        phonetic: "aj parbo na",
        meaning: "I cannot today.",
        prompt: "Say that you cannot today."
      },
      {
        at: 820,
        bangla: "কারণ আমার কাজ আছে।",
        phonetic: "karon amar kaj achhe",
        meaning: "Because I have work.",
        prompt: "Explain that it is because you have work."
      },
      {
        at: 1000,
        bangla: "কাল দেখা হবে।",
        phonetic: "kal dekha hobe",
        meaning: "See you tomorrow.",
        prompt: "Say, see you tomorrow."
      }
    ],
    dialogue: [
      {
        speaker: "Ammu",
        bangla: "কাল কখন আসবে?",
        phonetic: "kal kokhon ashbe?",
        meaning: "When will you come tomorrow?"
      },
      {
        speaker: "You",
        bangla: "আমি পাঁচটার দিকে আসব।",
        phonetic: "ami panch-tar dike ashbo",
        meaning: "I will come around five."
      },
      {
        speaker: "Ammu",
        bangla: "আজ আসবে না?",
        phonetic: "aj ashbe na?",
        meaning: "You will not come today?"
      },
      {
        speaker: "You",
        bangla: "আজ পারব না, কারণ আমার কাজ আছে।",
        phonetic: "aj parbo na, karon amar kaj achhe",
        meaning: "I cannot today, because I have work."
      },
      {
        speaker: "Ammu",
        bangla: "ঠিক আছে, কাল দেখা হবে।",
        phonetic: "thik achhe, kal dekha hobe",
        meaning: "Okay, see you tomorrow."
      }
    ],
    sources: []
  },
  61: {
    lessonId: 61,
    scene: "Family is planning dinner in fast, casual Bangla with one English word mixed in.",
    goal: "Catch the plan, answer naturally, and recover details without stopping the conversation.",
    phrases: [
      {
        at: 0,
        bangla: "কালকে আসবা তো?",
        phonetic: "kalke ashba to?",
        meaning: "You are coming tomorrow, right?",
        prompt: "How does Ammu check that you are coming tomorrow?"
      },
      {
        at: 155,
        bangla: "সবাই থাকবে।",
        phonetic: "shobai thakbe",
        meaning: "Everyone will be there.",
        prompt: "Say that everyone will be there."
      },
      {
        at: 320,
        bangla: "ডিনার একটু তাড়াতাড়ি হবে।",
        phonetic: "dinner ektu taratari hobe",
        meaning: "Dinner will be a little early.",
        prompt: "Say that dinner will be a little early."
      },
      {
        at: 485,
        bangla: "একটু দেরি হতে পারে।",
        phonetic: "ektu deri hote pare",
        meaning: "I might be a little late.",
        prompt: "Say that you might be a little late."
      },
      {
        at: 655,
        bangla: "আসার আগে ফোন দিও।",
        phonetic: "ashar age phone dio",
        meaning: "Call before you come.",
        prompt: "How does Ammu tell you to call before you come?"
      },
      {
        at: 820,
        bangla: "কিছু আনব?",
        phonetic: "kichhu anbo?",
        meaning: "Should I bring something?",
        prompt: "Ask if you should bring something."
      },
      {
        at: 1000,
        bangla: "না, কিছু লাগবে না।",
        phonetic: "na, kichhu lagbe na",
        meaning: "No, nothing is needed.",
        prompt: "Say that nothing is needed."
      }
    ],
    dialogue: [
      {
        speaker: "Ammu",
        bangla: "কালকে আসবা তো? সবাই থাকবে।",
        phonetic: "kalke ashba to? shobai thakbe",
        meaning: "You are coming tomorrow, right? Everyone will be there."
      },
      {
        speaker: "Ammu",
        bangla: "ডিনার একটু তাড়াতাড়ি হবে।",
        phonetic: "dinner ektu taratari hobe",
        meaning: "Dinner will be a little early."
      },
      {
        speaker: "You",
        bangla: "হ্যাঁ, আমি আসব। কিন্তু একটু দেরি হতে পারে।",
        phonetic: "hya, ami ashbo. kintu ektu deri hote pare",
        meaning: "Yes, I will come. But I might be a little late."
      },
      {
        speaker: "Ammu",
        bangla: "ঠিক আছে, আসার আগে ফোন দিও।",
        phonetic: "thik achhe, ashar age phone dio",
        meaning: "Okay, call before you come."
      },
      {
        speaker: "You",
        bangla: "আচ্ছা। কিছু আনব?",
        phonetic: "achha. kichhu anbo?",
        meaning: "Okay. Should I bring something?"
      },
      {
        speaker: "Ammu",
        bangla: "না, কিছু লাগবে না।",
        phonetic: "na, kichhu lagbe na",
        meaning: "No, nothing is needed."
      }
    ],
    sources: []
  }
};
